import { useEffect, useState, useCallback, useLayoutEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '../atoms';
import { api } from '../lib/api';
import { TOUR_EVENT } from '../help/tourState';

/**
 * WelcomeTour — the first-login explanation of what this product is.
 *
 * Mounted once in the operator shell. Opens on its own the first time a user
 * signs in (neither `onboarding_completed_at` nor `onboarding_dismissed_at` set
 * on their row), and on demand from the Help Center.
 *
 * ─── The spotlight, and the thing that makes it safe ────────────────────────
 * Steps may name a `target` — a `[data-tour="…"]` element that gets lifted out
 * of a dimmed overlay and ringed while the card explains it.
 *
 * A DOM-anchored tour is the kind that rots silently: rename a nav item, gate a
 * control behind a permission, or move a panel, and the tour cheerfully points
 * an arrow at nothing. Three things stop that here:
 *
 *   1. Targets are `data-tour` ATTRIBUTES, an explicit contract on the element,
 *      never a CSS class or DOM path the tour scrapes. Restyling cannot break it.
 *   2. If the element is absent, hidden, or zero-sized — which is exactly what a
 *      permission-gated control looks like to a user who lacks it — the step
 *      DEGRADES to the plain centred card. It never rings empty space, and the
 *      prose stands alone because every step is written to.
 *   3. `tourTargets.test.tsx` asserts every named target exists in the shell, so
 *      deleting an anchor fails the build rather than the tour.
 *
 * The tour navigates to the dashboard when it opens, so the targets it wants are
 * actually on screen — otherwise "look at your dashboard" fires on whatever page
 * the user happened to be on and degrades for no reason.
 */

interface TourStep {
  title: string;
  body: string;
  /** Optional plain-language "what to do here" line, rendered emphasised. */
  action?: string;
  /** `data-tour` value to spotlight. Omit for a concept step with no home on screen. */
  target?: string;
}

const STEPS: TourStep[] = [
  {
    title: 'Welcome to AppealStack',
    body: 'This app tracks one job: the government says a property is worth too much, you argue it down, and the client pays you a share of what you save them. Everything here supports that.',
    action: 'This takes about a minute. You can leave at any point and pick it up later from Help.',
  },
  {
    title: 'Three words worth knowing first',
    body: 'A client is the customer who pays you. A property is a building at an address they own. A parcel is a piece of land as the government records it, with its own ID number — and the parcel is the thing you actually appeal. One property can contain several parcels.',
    action: 'If you remember only one thing from this tour, make it this.',
  },
  {
    title: 'The menu on the left',
    body: 'This is how you get everywhere. It is grouped into sections — your daily workqueue at the top, then your portfolio, tax data, billing and settings further down.',
    action: 'Your menu only shows what you are allowed to use, so it may be shorter than a colleague’s. That is normal.',
    target: 'sidebar',
  },
  {
    title: 'Your numbers, at a glance',
    body: 'These four boxes answer "what should I do today" — how many appeals are live, what bills are waiting, and what work you have finished but not yet invoiced.',
    action: 'Every one of them is clickable. Clicking a box opens the list behind it.',
    target: 'dashboard-kpis',
  },
  {
    title: 'Deadlines do not move',
    body: 'Property tax runs on dates set by law. Miss the day to file a protest and the client pays the higher tax for the whole year — there is no appeal of a missed appeal. Anything in red here is a legal deadline.',
    action: 'Check this panel and the Calendar regularly.',
    target: 'upcoming-events',
  },
  {
    title: 'Finding things fast',
    body: 'Click here, or press Command-K on a Mac and Control-K on Windows, from anywhere in the app. Start typing a client, an address or a parcel number and jump straight to it.',
    action: 'It is almost always faster than clicking through the menu.',
    target: 'search',
  },
  {
    title: 'When you get stuck',
    body: 'This button opens help for whatever screen you are on. You will also see a small question mark next to headings and fields — that explains one thing, right where you are looking at it.',
    action: 'You do not need to know the official term. Searching "why can’t I see invoices" finds the right article.',
    target: 'help-button',
  },
];

interface OnboardingUser {
  onboarding_completed_at?: string | null;
  onboarding_dismissed_at?: string | null;
}

interface Rect { top: number; left: number; width: number; height: number }

const CARD_WIDTH = 460;
const GAP = 16;           // breathing room between the ring and the card
const RING = 6;           // how far the highlight ring sits outside the element

/**
 * Measure the spotlight target, re-measuring on resize and scroll.
 *
 * Returns null whenever there is nothing safe to highlight — no selector, no
 * matching element, or an element with no area (display:none, or a control the
 * caller's permissions removed). The caller reads null as "render the plain
 * centred card", which is the whole degradation story.
 */
function useSpotlight(target: string | undefined, active: boolean): Rect | null {
  const [rect, setRect] = useState<Rect | null>(null);

  useEffect(() => {
    if (!active || !target) { setRect(null); return; }

    let frame = 0;
    const measure = () => {
      const el = document.querySelector<HTMLElement>(`[data-tour="${target}"]`);
      if (!el) { setRect(null); return; }
      const r = el.getBoundingClientRect();
      // Zero-area means hidden or not laid out. Highlighting it would draw a
      // ring around a point, so treat it exactly like a missing element.
      if (r.width < 1 || r.height < 1) { setRect(null); return; }
      setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
    };

    // Bring the target into view, then measure SYNCHRONOUSLY. `scrollIntoView`
    // settles layout before it returns, so a frame buys nothing here — and
    // measuring inside a rAF would land the state update outside React's commit,
    // which shows up as an act() warning in tests and a one-frame flash of an
    // unpositioned card in the browser.
    const el = document.querySelector<HTMLElement>(`[data-tour="${target}"]`);
    el?.scrollIntoView({ block: 'nearest', behavior: 'auto' });
    measure();

    const onChange = () => { frame = requestAnimationFrame(measure); };
    window.addEventListener('resize', onChange);
    window.addEventListener('scroll', onChange, true);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('resize', onChange);
      window.removeEventListener('scroll', onChange, true);
    };
  }, [target, active]);

  return rect;
}

export interface Viewport { width: number; height: number }

/**
 * Where to put the card relative to the spotlight.
 *
 * Pure and viewport-injected so it can be unit-tested without a browser — the
 * bug this replaced (the card sitting ON TOP of the panel it was describing)
 * was invisible to every DOM test and obvious the moment the numbers were
 * printed.
 *
 * Tries each side in turn and takes the first that fits on screen AND does not
 * overlap the target. Overlap is the rejection criterion rather than an
 * afterthought: covering the thing you are pointing at defeats the spotlight
 * entirely, and "below unless it does not fit" quietly does exactly that for a
 * tall panel, where neither below nor above has room.
 */
export function computeCardPosition(
  rect: Rect | null,
  cardHeight: number,
  viewport: Viewport,
): React.CSSProperties {
  if (!rect) return { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };

  const { width: vw, height: vh } = viewport;
  const off = GAP + RING;
  const clampLeft = (l: number) => Math.max(GAP, Math.min(l, vw - CARD_WIDTH - GAP));
  const clampTop = (t: number) => Math.max(GAP, Math.min(t, vh - cardHeight - GAP));

  const candidates = [
    { left: clampLeft(rect.left), top: rect.top + rect.height + off },              // below
    { left: clampLeft(rect.left), top: rect.top - off - cardHeight },               // above
    { left: rect.left + rect.width + off, top: clampTop(rect.top) },                // right
    { left: rect.left - off - CARD_WIDTH, top: clampTop(rect.top) },                // left
  ];

  const onScreen = (c: { left: number; top: number }) =>
    c.left >= GAP && c.left + CARD_WIDTH <= vw - GAP &&
    c.top >= GAP && c.top + cardHeight <= vh - GAP;

  const overlapsTarget = (c: { left: number; top: number }) =>
    c.left < rect.left + rect.width + RING && c.left + CARD_WIDTH > rect.left - RING &&
    c.top < rect.top + rect.height + RING && c.top + cardHeight > rect.top - RING;

  for (const c of candidates) {
    if (onScreen(c) && !overlapsTarget(c)) return { left: c.left, top: c.top };
  }

  // Nothing clears the target — a target taller and wider than the space around
  // it. Sit in whichever horizontal half has more room and accept the overlap,
  // which is still better than falling off screen.
  const roomLeft = rect.left;
  const roomRight = vw - (rect.left + rect.width);
  return {
    left: roomRight >= roomLeft ? clampLeft(vw - CARD_WIDTH - GAP) : clampLeft(GAP),
    top: clampTop(rect.top),
  };
}

export function WelcomeTour() {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [cardHeight, setCardHeight] = useState(320);
  const cardRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  // Where the reader was when the tour took over, so it can hand them back.
  const returnTo = useRef<string | null>(null);

  const { data: user } = useQuery<OnboardingUser>({
    queryKey: ['current-user'],
    queryFn: ({ signal }) => api.get<OnboardingUser>('/api/current-user', signal),
    staleTime: Infinity,
  });

  const record = useMutation({
    mutationFn: (outcome: 'completed' | 'dismissed') =>
      api.post('/api/me/onboarding', { outcome }),
    onSettled: () => { queryClient.invalidateQueries({ queryKey: ['current-user'] }); },
  });

  const current = STEPS[step];
  const rect = useSpotlight(current?.target, open);

  // Measure the card so it can be placed above a target when there is no room
  // below. Runs in a layout effect so the reader never sees it jump.
  useLayoutEffect(() => {
    if (!open) return;
    const h = cardRef.current?.getBoundingClientRect().height;
    if (h && Math.abs(h - cardHeight) > 1) setCardHeight(h);
  }, [open, step, cardHeight]);

  useEffect(() => {
    if (!user) return;
    if (user.onboarding_completed_at || user.onboarding_dismissed_at) return;
    setOpen(true);
    setStep(0);
  }, [user]);

  useEffect(() => {
    const onOpen = () => { setStep(0); setOpen(true); };
    document.addEventListener(TOUR_EVENT, onOpen);
    return () => document.removeEventListener(TOUR_EVENT, onOpen);
  }, []);

  // Put the tour where its targets live. Without this, "look at your dashboard"
  // opens on whatever page the user was on and degrades for no reason.
  //
  // But only MOVE somebody who is not already there, and remember where they
  // were so they are handed back on exit. A first-login deep link into a
  // specific record is a reader who came for that record — silently landing them
  // on the dashboard and leaving them there loses what they were doing.
  // Navigating unconditionally also fires a router update on every open, which
  // is a pointless re-render when the path is already '/'.
  useEffect(() => {
    if (!open) return;
    if (location.pathname === '/') return;
    returnTo.current = location.pathname + location.search;
    navigate('/');
    // Intentionally not depending on `location`: this must run when the tour
    // OPENS, capturing where the reader was at that instant. Re-running as the
    // tour navigates would overwrite that with '/'.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const close = useCallback((outcome: 'completed' | 'dismissed', goTo?: string) => {
    const back = returnTo.current;
    returnTo.current = null;
    setOpen(false);
    record.mutate(outcome);
    // An explicit destination (the Finish button) wins; otherwise put the reader
    // back where the tour interrupted them.
    if (goTo) navigate(goTo);
    else if (back) navigate(back);
  }, [record, navigate]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close('dismissed');
      if (e.key === 'ArrowRight' && step < STEPS.length - 1) setStep((s) => s + 1);
      if (e.key === 'ArrowLeft' && step > 0) setStep((s) => s - 1);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, close, step]);

  if (!open || !current) return null;

  const isLast = step === STEPS.length - 1;

  return (
    // Backdrop click dismisses; Escape is the keyboard equivalent (handled above).
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 1000 }}
      // The whole surface blocks interaction so nobody navigates away mid-tour
      // and lands on a screen the next step does not describe.
      onClick={(e) => { if (e.target === e.currentTarget) close('dismissed'); }}
    >
      {rect ? (
        // The cutout: a transparent box over the target whose enormous shadow
        // dims everything else. One element does both the dimming and the ring,
        // so they can never drift out of alignment.
        <div
          aria-hidden="true"
          style={{
            position: 'fixed',
            top: rect.top - RING,
            left: rect.left - RING,
            width: rect.width + RING * 2,
            height: rect.height + RING * 2,
            borderRadius: 'var(--r-lg)',
            boxShadow: '0 0 0 9999px rgba(22, 34, 28, 0.62)',
            outline: '2px solid var(--brand-accent)',
            outlineOffset: 2,
            pointerEvents: 'none',
            // NO transition on the geometry, deliberately. Animating top/left/width/
            // height means the ring's PAINTED position lags its real one whenever
            // frames stop ticking — a backgrounded tab freezes the transition
            // mid-flight, and the highlight then sits on the previous step's target
            // until the page is looked at again. Snapping costs nothing and cannot
            // strand the spotlight somewhere it does not belong.
          }}
        />
      ) : (
        // Degraded: nothing safe to point at, so dim evenly and centre the card.
        <div aria-hidden="true" style={{ position: 'fixed', inset: 0, background: 'rgba(22, 34, 28, 0.62)' }} />
      )}

      <div
        ref={cardRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="welcome-tour-title"
        style={{
          position: 'fixed',
          width: CARD_WIDTH,
          maxWidth: 'calc(100vw - 32px)',
          background: 'var(--color-card)',
          borderRadius: 'var(--r-2xl)',
          boxShadow: 'var(--sh-lg)',
          padding: 'var(--sp-6)',
          ...computeCardPosition(rect, cardHeight, { width: window.innerWidth, height: window.innerHeight }),
        }}
      >
        <div style={{
          fontSize: 'var(--fs-sm)',
          fontWeight: 'var(--fw-semibold)',
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          color: 'var(--color-ink-3)',
          marginBottom: 'var(--sp-2)',
        }}>
          Step {step + 1} of {STEPS.length}
        </div>

        <h2
          id="welcome-tour-title"
          style={{
            margin: '0 0 var(--sp-3)',
            fontSize: 'var(--fs-2xl)',
            fontWeight: 'var(--fw-semibold)',
            letterSpacing: 'var(--ls-tight)',
            lineHeight: 'var(--lh-tight)',
            color: 'var(--color-ink)',
          }}
        >{current.title}</h2>

        <p style={{
          margin: '0 0 var(--sp-4)',
          fontSize: 'var(--fs-md)',
          lineHeight: 'var(--lh-relaxed)',
          color: 'var(--color-ink-2)',
        }}>{current.body}</p>

        {current.action && (
          <p style={{
            margin: '0 0 var(--sp-5)',
            padding: 'var(--sp-3) var(--sp-4)',
            background: 'var(--brand-primary-bg)',
            borderLeft: '3px solid var(--brand-accent)',
            borderRadius: 'var(--r-md)',
            fontSize: 'var(--fs-md)',
            lineHeight: 'var(--lh-relaxed)',
            color: 'var(--color-ink-2)',
          }}>{current.action}</p>
        )}

        <div style={{ display: 'flex', gap: 6, marginBottom: 'var(--sp-5)' }} aria-hidden="true">
          {STEPS.map((_, i) => (
            <span
              key={i}
              style={{
                height: 4,
                flex: 1,
                borderRadius: 2,
                background: i <= step ? 'var(--brand-primary)' : 'var(--color-border)',
              }}
            />
          ))}
        </div>

        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 'var(--sp-3)',
          flexWrap: 'wrap',
        }}>
          <Button variant="ghost" size="sm" onClick={() => close('dismissed')}>
            Skip for now
          </Button>

          <div style={{ display: 'flex', gap: 'var(--sp-2)' }}>
            {step > 0 && (
              <Button variant="secondary" size="md" onClick={() => setStep((s) => s - 1)}>
                Back
              </Button>
            )}
            {isLast ? (
              <Button
                variant="primary"
                size="md"
                onClick={() => close('completed', '/help')}
              >Finish and open Help</Button>
            ) : (
              <Button variant="primary" size="md" onClick={() => setStep((s) => s + 1)}>
                Next
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
